<template>
    <div
        class="entry"
        :class="{
            'is-decision': isDecision,
            'is-edit': !isDecision,
            'selected': isSelected,
            'comparison': isComparison,
            'current': isCurrent,
        }"
        @mouseenter="prefetchSummary"
        @focusin="prefetchSummary"
    >
        <!-- Rail node: a ringed ◆ for a decision, a small dot for an edit; the
             ring fills with the accent ONLY for the version being viewed
             (isSelected). The latest version is marked by its "latest" tag, not
             by a second filled dot. -->
        <span class="node" :class="{ filled: isSelected }">
            <i v-if="isDecision" class="pi pi-sitemap node-glyph" />
        </span>

        <!-- DECISION: an accent card that dominates the timeline. -->
        <div v-if="isDecision" class="card">
            <RouterLink class="card-main" :to="to">
                <div class="headline-row">
                    <span class="what">{{ commit.decision!.what }}</span>
                    <span v-if="isSelected" class="tag tag-viewing"
                        >viewing</span
                    >
                    <span v-else-if="isCurrent" class="tag tag-latest"
                        >latest</span
                    >
                </div>
                <p
                    v-if="commit.decision!.why"
                    class="why"
                    :title="commit.decision!.why"
                >
                    {{ commit.decision!.why }}
                </p>
            </RouterLink>

            <!-- Byline + actions (thread link, expand toggle) are always
                 visible; hover/focus still prefetches the thread for the
                 tooltip and an instant expand. -->
            <div class="meta">
                <span class="byline"
                    >{{ commit.author.name }} · {{ formattedDate }}</span
                >
                <div class="actions">
                    <!-- Collapsed only: once expanded, the panel below carries
                         the thread (title + status + Open), so the icon-only
                         pill would be redundant. -->
                    <button
                        v-if="!expanded"
                        type="button"
                        class="thread-pill"
                        :title="threadTooltip"
                        :aria-label="threadTooltip"
                        data-testid="open-thread"
                        @click="openThread"
                    >
                        <i class="pi pi-comments" />
                        <i class="pi pi-external-link pill-ext" />
                    </button>
                    <button
                        type="button"
                        class="expand-toggle"
                        :aria-expanded="expanded"
                        aria-label="Toggle decision details"
                        data-testid="expand-decision"
                        @click="toggleExpand"
                    >
                        <i
                            class="pi"
                            :class="
                                expanded ? 'pi-chevron-up' : 'pi-chevron-down'
                            "
                        />
                    </button>
                </div>
            </div>

            <!-- Expanded: the "all data" as a tidy label/value grid. -->
            <div v-if="expanded" class="details" data-testid="decision-details">
                <div v-if="isLoading" class="details-loading">
                    <i class="pi pi-spin pi-spinner" /> Loading thread…
                </div>
                <dl v-else-if="summary" class="detail-grid">
                    <dt>Thread</dt>
                    <dd class="dd-thread">
                        <span class="thread-title">{{ summary.title }}</span>
                        <span
                            class="status-pill"
                            :class="`status-${summary.status}`"
                            >{{ summary.status }}</span
                        >
                        <button
                            type="button"
                            class="open-thread-link"
                            data-testid="open-thread-detail"
                            @click="openThread"
                        >
                            Open <i class="pi pi-external-link" />
                        </button>
                    </dd>

                    <template v-if="affectedEntities.length">
                        <dt>Changed</dt>
                        <dd class="dd-changed" data-testid="changed-entities">
                            <span
                                v-for="e in affectedEntities"
                                :key="`${e.entity_type}:${e.entity_id}`"
                                class="chip"
                                :class="`chip-${e.change_type ?? 'other'}`"
                            >
                                <span class="chip-dot" />
                                {{ e.name || e.entity_id }}
                                <span class="chip-mark">{{
                                    changeMark(e.change_type)
                                }}</span>
                            </span>
                        </dd>
                    </template>

                    <dt>Committed</dt>
                    <dd>{{ formattedCommittedAt }}</dd>
                </dl>
                <p v-else class="details-fallback">
                    Thread {{ shortThreadId }} — details unavailable.
                </p>
            </div>
        </div>

        <!-- DIRECT EDIT: a soft, compact line so it reads as minor next to a
             decision card — pencil in a muted badge, subject, author + date.
             The toggle expands the line in place to the full commit message
             (no separate panel, so the text is never shown twice). -->
        <div v-else class="edit-head">
            <RouterLink
                class="edit-line"
                :class="{ expanded: editExpanded }"
                :to="to"
            >
                <span class="edit-badge"><i class="pi pi-pencil" /></span>
                <span
                    class="edit-subject"
                    :title="commit.subject"
                    data-testid="edit-message"
                    >{{ editExpanded ? editFullMessage : commit.subject }}</span
                >
                <span class="edit-byline"
                    >{{ commit.author.name }} · {{ formattedDate }}</span
                >
            </RouterLink>
            <button
                type="button"
                class="expand-toggle"
                :aria-expanded="editExpanded"
                aria-label="Toggle full message"
                data-testid="expand-edit"
                @click="editExpanded = !editExpanded"
            >
                <i
                    class="pi"
                    :class="editExpanded ? 'pi-chevron-up' : 'pi-chevron-down'"
                />
            </button>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useRoute } from "vue-router";

import type { HistoryEntryWithDate } from "@/lib/artefacts/user";
import {
    type AffectedEntitySchema,
    type ThreadSchema,
    useThreadsStore,
} from "@/stores/threads-store";

const props = defineProps<{
    commit: HistoryEntryWithDate;
    isSelected: boolean;
    isCurrent: boolean;
    isComparison: boolean;
}>();

const route = useRoute();
const store = useThreadsStore();

const isDecision = computed(() => !!props.commit.decision);

// The version-select route target (unchanged from the old flat list): keep the
// current route + params, swap in this commit's id as the viewed version.
const to = computed(() => ({
    name: route.name ?? undefined,
    params: { ...route.params, version: props.commit.id },
    query: route.query,
}));

const dateFormat: Intl.DateTimeFormatOptions = {
    day: "numeric",
    month: "short",
    hour: "numeric",
    hour12: false,
    minute: "2-digit",
};
const formattedDate = computed(() =>
    props.commit.author.date.toLocaleString(undefined, dateFormat)
);
const formattedCommittedAt = computed(() =>
    props.commit.committer.date.toLocaleString(undefined, dateFormat)
);

const shortThreadId = computed(() =>
    (props.commit.decision?.thread_id ?? "").slice(0, 8)
);

// Direct-edit expand: the collapsed line shows the (truncated) subject; expanding
// swaps it in place for the full commit message. Gitaly's `body` is the whole
// message (subject line included), so it already IS the full text — no need to
// also render the subject separately (that was the earlier duplication).
const editExpanded = ref(false);
const editFullMessage = computed(
    () => (props.commit.body ?? "").trim() || props.commit.subject
);

// Open the (now closed) thread this decision landed in, in the threads drawer.
const openThread = () => {
    const threadId = props.commit.decision?.thread_id;
    if (threadId) store.openDrawer(threadId);
};

// Lazy enrichment. Fetched at most once, cached on the component. Triggered on
// hover/focus (so the pill can show the thread title before you even expand)
// and on expand (so expanding is instant once prefetched).
const expanded = ref(false);
const isLoading = ref(false);
const summary = ref<ThreadSchema | null>(null);
const hasFetched = ref(false);

const affectedEntities = computed<AffectedEntitySchema[]>(
    () => summary.value?.affected_entities ?? []
);

// Collapsed, the thread is an icon-only pill; its tooltip carries the title
// once we've prefetched it (falling back to a generic label before then).
const threadTooltip = computed(() =>
    summary.value ? `Open thread: ${summary.value.title}` : "Open thread"
);

const ensureSummary = async () => {
    if (hasFetched.value || !isDecision.value) return;
    const threadId = props.commit.decision?.thread_id;
    if (!threadId) return;
    isLoading.value = true;
    try {
        summary.value = await store.fetchThreadSummary(threadId);
    } finally {
        hasFetched.value = true;
        isLoading.value = false;
    }
};

const prefetchSummary = () => {
    void ensureSummary();
};

const changeMark = (changeType: string | null | undefined): string => {
    switch (changeType) {
        case "added":
            return "+";
        case "removed":
            return "−";
        case "modified":
            return "~";
        default:
            return "";
    }
};

const toggleExpand = async () => {
    expanded.value = !expanded.value;
    if (expanded.value) await ensureSummary();
};
</script>

<style lang="scss" scoped>
.entry {
    position: relative;
    padding: 4px 8px 4px 34px;
}

/* --- Rail node --- */
.node {
    position: absolute;
    top: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: Canvas;
    border-radius: 50%;

    .is-decision & {
        left: 2px;
        width: 16px;
        height: 16px;
        border: 2px solid var(--stekz-primary-color);
    }

    .is-edit & {
        left: 6px;
        width: 8px;
        height: 8px;
        border: 2px solid var(--surface-border);
    }

    &.filled {
        background: var(--stekz-primary-color);
        border-color: var(--stekz-primary-color);
    }
}

.node-glyph {
    font-size: 0.55rem;
    color: var(--stekz-primary-color);

    .filled & {
        color: var(--stekz-white);
    }
}

/* --- Decision card --- */
.card {
    border: 1px solid transparent;
    border-left: 3px solid var(--stekz-primary-color);
    border-radius: 8px;
    background: var(--surface-background);
    padding: 8px 10px;
    transition: 150ms ease-in-out;

    .selected & {
        background: var(--stekz-primary-background);
        border-color: var(--stekz-primary-color);
        box-shadow: inset 0 0 0 1px var(--stekz-primary-color);
    }

    .comparison & {
        background: var(--stekz-primary-background);
    }
}

.card-main {
    display: block;
    text-decoration: none;
    color: inherit;
}

.headline-row {
    display: flex;
    align-items: baseline;
    gap: 0.5rem;
}

.what {
    font-weight: 600;
    font-size: 0.98rem;
    line-height: 1.3;

    .selected & {
        color: var(--stekz-primary-color);
    }
}

.tag {
    margin-left: auto;
    flex: 0 0 auto;
    font-size: 0.62rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    padding: 1px 6px;
    border-radius: 999px;
}

.tag-viewing {
    background: var(--stekz-primary-color);
    color: var(--stekz-white);
}

.tag-latest {
    background: var(--surface-border);
    color: var(--text-color-secondary);
}

.why {
    margin: 3px 0 0;
    color: var(--text-color-secondary);
    font-size: 0.85rem;
    line-height: 1.35;
}

.meta {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 6px;
    min-height: 24px;
}

.byline {
    color: var(--text-color-secondary);
    font-size: 0.76rem;
}

.actions {
    display: flex;
    align-items: center;
    gap: 0.35rem;
    margin-left: auto;
}

.thread-pill {
    display: inline-flex;
    align-items: center;
    gap: 0.15rem;
    padding: 2px 7px;
    border-radius: 999px;
    border: 1px solid var(--surface-border);
    background: Canvas;
    color: var(--stekz-primary-color);
    font-size: 0.74rem;
    cursor: pointer;
    transition: 120ms ease-in-out;

    &:hover {
        background: var(--stekz-primary-background);
    }

    .pill-ext {
        font-size: 0.55rem;
        flex: 0 0 auto;
    }
}

.expand-toggle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border: none;
    background: transparent;
    color: var(--text-color-secondary);
    cursor: pointer;
    border-radius: 4px;

    &:hover {
        background: var(--surface-background);
    }
}

/* --- Expanded details --- */
.details {
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px dashed var(--surface-border);
    font-size: 0.82rem;
}

.details-loading,
.details-fallback {
    color: var(--text-color-secondary);
    margin: 0;
}

.detail-grid {
    display: grid;
    grid-template-columns: max-content 1fr;
    gap: 4px 12px;
    margin: 0;

    dt {
        color: var(--text-color-secondary);
        font-size: 0.7rem;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        padding-top: 2px;
    }

    dd {
        margin: 0;
    }
}

.dd-thread {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-wrap: wrap;

    .thread-title {
        font-weight: 600;
    }
}

.status-pill {
    font-size: 0.68rem;
    padding: 0 6px;
    border-radius: 999px;
    text-transform: capitalize;
    background: var(--surface-border);

    &.status-accepted {
        background: var(--green-100, #d1fadf);
        color: var(--green-700, #027a48);
    }

    &.status-rejected {
        background: var(--red-100, #fee4e2);
        color: var(--red-700, #b42318);
    }
}

.open-thread-link {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    border: none;
    background: transparent;
    color: var(--stekz-primary-color);
    font-size: 0.72rem;
    cursor: pointer;

    &:hover {
        text-decoration: underline;
    }
}

.dd-changed {
    display: flex;
    flex-wrap: wrap;
    gap: 0.35rem;
}

.chip {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    padding: 1px 8px;
    border-radius: 6px;
    background: var(--surface-border);
    font-size: 0.74rem;

    .chip-dot {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: var(--text-color-secondary);
    }

    .chip-mark {
        font-family: monospace;
        color: var(--text-color-secondary);
    }
}

.chip-added .chip-dot {
    background: var(--green-500, #12b76a);
}
.chip-modified .chip-dot {
    background: var(--amber-500, #f79009);
}
.chip-removed .chip-dot {
    background: var(--red-500, #f04438);
}

/* --- Direct-edit line --- */
.edit-head {
    display: flex;
    align-items: center;
    gap: 0.15rem;
}

.edit-line {
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 4px 8px;
    text-decoration: none;
    color: inherit;
    font-size: 0.84rem;
    border-radius: 8px;
    transition: background 120ms ease-in-out;

    &:hover {
        background: var(--surface-background);
    }

    .selected & {
        background: var(--stekz-primary-background);
        color: var(--stekz-primary-color);
        font-weight: 600;
    }

    /* Expanded: let the message wrap to its full length in place. */
    &.expanded {
        align-items: flex-start;

        .edit-subject {
            white-space: pre-wrap;
            overflow: visible;
            text-overflow: clip;
        }

        .edit-byline {
            padding-top: 1px;
        }
    }
}

/* Pencil in a soft muted circle — a quiet counterpart to a decision's ◆ node. */
.edit-badge {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: var(--surface-background);

    i {
        font-size: 0.62rem;
        color: var(--text-color-secondary);
    }
}

.edit-subject {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.edit-byline {
    margin-left: auto;
    flex: 0 0 auto;
    font-size: 0.74rem;
    color: var(--text-color-secondary);
    white-space: nowrap;
}
</style>
